import { useCallback, useMemo, useRef } from "react";
import { proxy, useSnapshot } from "valtio";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { usePreviewContext } from "../../../contexts/preview-context";

/** Shared state */
const photoPanningState = proxy({
	isPhotoPanning: false,
	actions: {
		startPhotoPanning() {
			photoPanningState.isPhotoPanning = true;
		},
		endPhotoPanning() {
			photoPanningState.isPhotoPanning = false;
		},
	},
});

/**
 * Logic to pan a photo
 * This intentionally doesn't include the logic to trigger it
 * Could be based on gestures, keyboard, ...
 */
export function usePhotoPanning({
	maxReplaceDelay = 1000,
}: {
	/** When using in a transaction, we can pass Infinity to keep the command replaceable in the undo stack until transaction is closed */
	maxReplaceDelay?: number;
}) {
	const dashboard = useDashboardContext();
	const { getSpreadToScreenTransform } = usePreviewContext();

	/** An object holding information information for Panning/Zooming a photo in a zone */
	const zonePanCropRef = useRef<eXpress.creator.rendering.ZonePanCrop | undefined>(undefined);

	const { isPhotoPanning } = useSnapshot(photoPanningState);

	const start = useCallback(
		function startPhotoPanning({
			selectedZone,
			pointOnScreen,
		}: {
			selectedZone: eXpress.core.PhotoLookZone;
			pointOnScreen: eXpress.math.Point;
		}) {
			photoPanningState.actions.startPhotoPanning();

			// Start a new PhotoPanning
			zonePanCropRef.current = dashboard.createZonePanCrop(selectedZone, {
				startPos: pointOnScreen,
				dipToCssScale: eXpress.math.Vector.ones, // didn't use, I think included inside getLocalToDocumentTransform
				getLocalToDocumentTransform: () => {
					const { spread } = selectedZone;
					if (!spread) return eXpress.math.Frame.identity;

					const spreadToScreenTransform = getSpreadToScreenTransform(spread, undefined);
					if (!spreadToScreenTransform) return eXpress.math.Frame.identity;

					return eXpress.math.scale(spreadToScreenTransform.scale);
				},
				maxReplaceDelay,
			});
		},
		[dashboard, getSpreadToScreenTransform, maxReplaceDelay],
	);

	const update = useCallback(function movePhotoPanning(point: eXpress.math.Point) {
		zonePanCropRef.current?.moveTo(point, false);
	}, []);

	const end = useCallback(function endPhotoPanning() {
		if (!zonePanCropRef.current) return;
		zonePanCropRef.current.dispose();
		zonePanCropRef.current = undefined;
		photoPanningState.actions.endPhotoPanning();
	}, []);

	const cancel = useCallback(function cancelPhotoPanning() {
		if (!zonePanCropRef.current) return;
		zonePanCropRef.current.cancel();
		zonePanCropRef.current.dispose();
		zonePanCropRef.current = undefined;
		photoPanningState.actions.endPhotoPanning();
	}, []);

	return [
		isPhotoPanning,
		useMemo(
			// Try to return a stable object
			() => ({
				start,
				update,
				end,
				cancel,
			}),
			[start, update, end, cancel],
		),
	] as const;
}
